import {useEffect, useRef, useState} from "react";
import {Server, User} from "../../api/types";
import Loading from "../Loading";
import {agent} from "../../api/agent";
import styled from "styled-components";
import Button from "../Button";
import {useNavigate} from "react-router-dom";
import {Modal} from "../Modal";
import Input from "../Input";
import Pane from "../table/Pane";
import GeneralPermissions from "../permissions/GeneralPermissions";
import RepoList from "../permissions/repo/RepoList";
import DeploymentList from "../permissions/deployment/DeploymentList";
import ServerList from "../permissions/server/ServerList";
import TaskList from "../permissions/task/TaskList";
import UserList from "../permissions/user/UserList";

interface Props {
    userId: string;
}

const Center = styled.div`
    display: flex;
    justify-content: center;
`;

const Content = styled.div`

    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: var(--content-box-bg);
    padding: 1rem;
    margin-top: 1rem;
    min-width: 40rem;

    .label {
        font-weight: bold;
        display: block;
        margin-bottom: 0.5rem;
    }

    .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
    }

    input {
        background-color: transparent;
        outline: none;
        border: none;
        font-size: 1.1rem;
        margin: .2rem .5rem;
        padding: .2rem;
        color: #f2f2f2;
        border-bottom: #f2f2f2 solid 1px;
    }
`;

export default function UserEditor({userId}: Props) {

    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);

    const navigate = useNavigate();

    useEffect(() => {
        setPasswordModalOpen(false);
        setLoading(true);
        if (userId !== 'new') {
            agent.getUser(userId).then(value => {
                setUser(value);
                usernameRef.current!.value = value.username;
                superAdminRef.current!.checked = value.superAdmin;
                requirePasswordChangeRef.current!.checked = value.requirePasswordChange;
                setPermissions(value.permissions);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [userId]);

    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const passwordModelRef = useRef<HTMLInputElement>(null);
    const superAdminRef = useRef<HTMLInputElement>(null);
    const requirePasswordChangeRef = useRef<HTMLInputElement>(null);
    const [changedPassword, setChangedPassword] = useState<string | null>('');
    const [permissions, setPermissions] = useState<string[]>([]);

    const save = () => {
        setLoading(true);
        if (!user) {
            const newUser: User = {
                id: '',
                username: usernameRef.current?.value || '',
                password: passwordRef.current?.value || '',
                permissions: permissions,
                superAdmin: superAdminRef.current?.checked || false,
                requirePasswordChange: requirePasswordChangeRef.current?.checked || false,
            };
            agent.postUser(newUser).then(value => {
                navigate('/users');
                setLoading(false);
            }).catch(reason => {
                setLoading(false);
            });
        } else {
            const updatedUser: User = {
                ...user,
                username: usernameRef.current?.value || user.username,
                permissions: permissions,
                superAdmin: superAdminRef.current?.checked || user.superAdmin,
                requirePasswordChange: requirePasswordChangeRef.current?.checked || user.requirePasswordChange,
            };
            if (changedPassword) {
                updatedUser.password = changedPassword;
            }
            agent.putUser(updatedUser).then(value => {
                navigate('/users');
                setLoading(false);
            });
        }
    }

    const deleteServer = () => {
        agent.deleteUser(userId).then(() => {
            navigate('/users');
        });
    }

    return (
        <>
            <Modal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)}>
                <h1>Set Password</h1>
                <Input placeholder={'Enter new password'} ref={passwordModelRef}/>
                <div style={{display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: '.5rem'}}>
                    <Button className={'primary'} onClick={() => {
                        setChangedPassword(passwordModelRef.current!.value);

                        setPasswordModalOpen(false);
                    }}>Save</Button>
                </div>
            </Modal>
            <Loading open={loading}></Loading>
            <Center>
                <Content>
                    <div className={'grid'}>
                        <div>
                            <span className={'label'}>Username</span>
                            <input placeholder={'Enter username'} ref={usernameRef}/>
                        </div>

                        {!user && (
                            <div>
                                <span className={'label'}>Password</span>
                                <input placeholder={'Enter password'} ref={passwordRef}/>
                            </div>
                        )}
                        {user && (
                            <div>
                                <Button onClick={() => setPasswordModalOpen(true)}>Set Password</Button>
                            </div>
                        )}

                    </div>
                    <div className={'grid'}>
                        <div>
                            <span className={'label'}>Is Superadmin</span>
                            <input type={'checkbox'} defaultChecked={user?.superAdmin || false} ref={superAdminRef}/>
                        </div>
                        <div>
                            <span className={'label'}>Require Password change</span>
                            <input type={'checkbox'} defaultChecked={user?.requirePasswordChange || false} ref={requirePasswordChangeRef}/>
                        </div>
                    </div>
                    <div>
                        <h1>Permissions</h1>
                        <Pane panes={[
                            {
                                name: 'General',
                                render: () => (<GeneralPermissions permissions={permissions}
                                                                   onChange={(perms: string[]) => {
                                                                       setPermissions(perms);
                                                                   }}></GeneralPermissions>)
                            },
                            {name: 'Repo', render: () => (<RepoList permissions={permissions}
                                                                    onChange={(perms: string[]) => {
                                                                        setPermissions(perms);
                                                                    }}></RepoList>)},
                            {name: 'Deployments', render: () => (<DeploymentList permissions={permissions}
                                                                                 onChange={(perms: string[]) => {
                                                                                     setPermissions(perms);
                                                                                 }}></DeploymentList>)},
                            {name: 'Servers', render: () => (<ServerList permissions={permissions}
                                                                         onChange={(perms: string[]) => {
                                                                             setPermissions(perms);
                                                                         }}></ServerList>)},
                            {name: 'Tasks', render: () => (<TaskList permissions={permissions}
                                                                         onChange={(perms: string[]) => {
                                                                             setPermissions(perms);
                                                                         }}></TaskList>)},
                            {name: 'Users', render: () => (<UserList permissions={permissions}
                                                                         onChange={(perms: string[]) => {
                                                                             setPermissions(perms);
                                                                         }}></UserList>)},
                        ]}></Pane>
                    </div>
                    <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                        <Button className={'default'} onClick={() => {
                            navigate('/users')
                        }}>Cancel</Button>
                        <Button className={'red'} onClick={deleteServer}>Delete</Button>
                        <Button className={'primary'} onClick={save}>Save</Button>
                    </div>
                </Content>
            </Center>
        </>
    );
}

